import { useMemo, useState } from 'react';
import HeaderMobile from '@/shared/components/ui/HeaderMobile/HeaderMobile';
import { useNavigate, useSearchParams } from 'react-router-dom';
import WrapperMobile from '@/shared/components/layout/WrapperMobile/WrapperMobile';
import TopBarHeader from '@/domains/mobile-web/components/TopBarHeader/TopBarHeader';
import {
    CardProductTransactionItem,
    type TransactionItemProduct,
} from '@/domains/mobile-web/components/CardProductTransaction/CardProductTransaction';
import AddressCard, { type AddressData } from '@/domains/mobile-web/components/AddressCard/AddressCard';
import ShippingSelector from '@/domains/mobile-web/components/ShippingSelector/ShippingSelector';
import BottomPayment from '@/domains/mobile-web/components/BottomPayment/BottomPayment';
import { useAddresses } from '@/domains/mobile-web/hooks/useAddresses';
import { useCart } from '@/domains/mobile-web/hooks/useCart';
import { useCheckout } from '@/domains/mobile-web/hooks/useOrders';
import { useProduct } from '@/domains/mobile-web/hooks/useProducts';
import { useShippingRates } from '@/domains/mobile-web/hooks/useShipping';
import type { Address } from '@/domains/mobile-web/types/address.types';
import { formatAddress } from '@/domains/mobile-web/utils/address';
import { authorLabel } from '@/domains/mobile-web/utils/bundle';
import { formatPrice } from '@/domains/mobile-web/utils/price';
import { courierOptions, serviceId } from '@/domains/mobile-web/utils/shipping';
import { showToast } from '@/shared/utils/showToast';
import { randomUuid } from '@/shared/utils/uuid';
import { useCheckoutStore } from '@/store/checkoutStore';

interface CheckoutLine {
    key: string;
    productId: string;
    product: TransactionItemProduct;
    quantity: number;
}

const toAddressData = (address: Address): AddressData => ({
    label: address.label ?? undefined,
    recipientName: address.recipient_name,
    phone: address.phone_number,
    fullAddress: formatAddress(address),
    postalCode: address.postal_code,
});

/** ?items=<cart item ids> (Keranjang, selected lines) or ?product=<slug> (Beli, one copy). */
const useCheckoutLines = () => {
    const [searchParams] = useSearchParams();
    const slug = searchParams.get('product') ?? undefined;
    const itemsParam = searchParams.get('items') ?? '';

    const cart = useCart();
    const product = useProduct(slug);

    const lines = useMemo<CheckoutLine[]>(() => {
        if (slug) {
            const detail = product.data;
            return detail
                ? [
                    {
                        key: detail.id,
                        productId: detail.id,
                        quantity: 1,
                        product: {
                            title: detail.title,
                            author: authorLabel(detail),
                            frontCover: detail.images[0] ?? null,
                            price: Number(detail.final_price),
                        },
                    },
                ]
                : [];
        }
        // Lines of products that became hidden or sold out are not in the cart answer
        const itemIds = itemsParam.split(',');
        return (cart.data?.items ?? [])
            .filter((item) => itemIds.includes(item.id))
            .map((item) => ({
                key: item.id,
                productId: item.product_id,
                quantity: item.quantity,
                product: {
                    title: item.title,
                    author: authorLabel(item),
                    frontCover: item.image_url,
                    price: Number(item.unit_price),
                },
            }));
    }, [slug, product.data, cart.data, itemsParam]);

    const source = slug ? product : itemsParam ? cart : null;
    return { lines, isDirect: !!slug, isLoading: !!source?.isLoading, isError: !!source?.isError };
};

const CheckOut = () => {
    const navigate = useNavigate();
    const { lines, isDirect, isLoading, isError } = useCheckoutLines();
    // One key per checkout page, so a retried Bayar Sekarang cannot place a second order
    const [idempotencyKey] = useState(randomUuid);
    const placeOrder = useCheckout();

    const addresses = useAddresses();
    const pickedAddressId = useCheckoutStore((state) => state.addressId);
    const rows = addresses.data?.rows ?? [];
    // The address picked on Daftar Alamat while it still exists, else the main address
    const address = rows.find((row) => row.id === pickedAddressId) ?? rows.find((row) => row.is_default);

    const shippingLines = useMemo(
        () => lines.map((line) => ({ product_id: line.productId, quantity: line.quantity })),
        [lines],
    );
    const rates = useShippingRates(address?.id, shippingLines);
    const options = useMemo(() => courierOptions(rates.data?.rows ?? []), [rates.data]);

    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
    // A new quote (other address or lines) may not offer the chosen service any more
    const selectedService = options
        .flatMap((option) => option.services)
        .find((service) => service.id === selectedServiceId);

    const selectedRate = rates.data?.rows.find((rate) => serviceId(rate) === selectedService?.id);

    const totalPrice = lines.reduce((sum, line) => sum + line.product.price * line.quantity, 0);
    const shippingPrice = selectedService?.price ?? 0;
    const totalPayment = totalPrice + shippingPrice;
    const canPay = lines.length > 0 && !!address && !!selectedRate && !placeOrder.isPending;

    // The order waits for the bank transfer: Pembayaran shows the account and the amount
    const handlePay = () => {
        const [firstLine] = lines;
        if (!canPay || !address || !selectedRate || !firstLine) return;
        const items = isDirect
            ? { source: 'direct' as const, product_id: firstLine.productId, quantity: firstLine.quantity }
            : { source: 'cart' as const, cart_item_ids: lines.map((line) => line.key) };
        placeOrder.mutate(
            {
                idempotency_key: idempotencyKey,
                address_id: address.id,
                courier_code: selectedRate.courier_code,
                service_code: selectedRate.service_code,
                ...items,
            },
            {
                onSuccess: (order) => {
                    showToast({
                        type: 'success',
                        title: 'Pesanan dibuat',
                        message: `Pesanan ${order.order_number} menunggu pembayaran.`,
                    });
                    navigate(`/store/payment?id=${order.order_id}`, { replace: true });
                },
            },
        );
    };

    const renderShipping = () => {
        if (!address) {
            return <p className="body1 text-text-grey">Pilih alamat pengiriman untuk melihat ongkos kirim.</p>;
        }
        if (rates.isLoading) return <p className="body1 text-text-grey">Memuat ongkos kirim...</p>;
        if (rates.isError) {
            return <p className="body1 text-text-grey">Gagal memuat ongkos kirim. {rates.error.message}</p>;
        }
        if (options.length === 0) {
            return <p className="body1 text-text-grey">Tidak ada kurir yang melayani alamat ini.</p>;
        }
        return (
            <ShippingSelector
                options={options}
                selectedServiceId={selectedService?.id}
                onSelectService={(service) => setSelectedServiceId(service.id)}
            />
        );
    };

    return (
        <main className="min-h-screen">
            <HeaderMobile>
                <TopBarHeader type="back" onBack={() => navigate(-1)} title="Checkout" />
            </HeaderMobile>

            <WrapperMobile className="pb-[120px]">
                {isLoading ? null : isError ? (
                    <p className="body1 text-text-grey">Gagal memuat produk.</p>
                ) : lines.length === 0 ? (
                    <p className="body1 text-text-grey">Tidak ada produk untuk dibayar.</p>
                ) : (
                    <>
                        <div className="flex shadowCard flex-col  p-3 w-full">
                            <h3 className="title2medium capitalize text-black">Daftar Produk</h3>
                            {lines.map((line, index) => (
                                <CardProductTransactionItem
                                    key={line.key}
                                    product={line.product}
                                    quantity={line.quantity}
                                    isLast={index === lines.length - 1}
                                />
                            ))}
                        </div>

                        <div className="flex flex-col w-full">
                            <AddressCard
                                address={address ? toAddressData(address) : null}
                                onClick={() => navigate('/store/address?select=1')}
                            />
                        </div>

                        <div className="shadowCard p-3 flex flex-col gap-3">
                            <h3 className='title2medium text-black'>Metode Pengiriman</h3>
                            {renderShipping()}
                        </div>

                        <div className="shadowCard p-3 flex flex-col gap-3">
                            <h3 className='title2medium text-black'>Rincian Pembayaran</h3>
                            <div className="inline-flex gap-3 justify-between">
                                <span className='reg12 text-text-grey'>Total Harga </span>
                                <span>Rp {formatPrice(totalPrice)}</span>
                            </div>
                            <div className="inline-flex gap-3 justify-between">
                                <span className='reg12 text-text-grey'>Ongkos Kirim </span>
                                <span>{selectedService ? `Rp ${formatPrice(shippingPrice)}` : '-'}</span>
                            </div>
                            <div className="border-blue-cs-200 border-[0.5px]"></div>
                            <div className="inline-flex gap-3 justify-between">
                                <span className='title1bold text-black'>Total Bayar </span>
                                <span className='title1bold text-black'>Rp {formatPrice(totalPayment)}</span>
                            </div>
                        </div>
                    </>
                )}
            </WrapperMobile>

            <BottomPayment
                textButton='Bayar Sekarang'
                title='Total tagihan'
                stylePrice='text-text-danger-500'
                disabled={!canPay}
                onClick={handlePay}
                totalPrice={totalPayment}
            />
        </main>
    );
};

export default CheckOut;
